import { compileTrainingStructure, STRUCTURED_TRAINING_TEMPLATES, trainingPreset } from "./trainingStructure";
import { runningPreset, runningValidation } from "./runningTraining";
import { RUNNING_TRAINING_CHOICES, runningTrainingChoice } from "./runningTrainingSelection";
import { effectiveTrainingTemplates, runningSystemId, TRAINING_GROUPS, UNASSIGNED_TRAINING_GROUP } from "./trainingLibrarySelection";
import { calendarV2Copy } from "./calendarV2Copy";

export const LIBRARY_API = "/admin/training-library";
export const emptyLibrary = () => ({ templates: [], exercises: [], groups: [], media: [], media_upload_available: false });
const textFields = ["title", "title_en", "description", "description_en", "short_description", "short_description_en"];
const activityKeys = ["training_unit_type", "running_variant", "running_details", "training_template", "training_structure", "recovery_type"];
export const cloneLibraryValue = (value) => JSON.parse(JSON.stringify(value));
export const libraryReadOnly = (type, item) => type === "groups" && TRAINING_GROUPS.some((group) => group.id === item?.id);

export function libraryRunningTemplate(items, choice) {
  const id = runningSystemId(choice);
  return id ? effectiveTrainingTemplates(items).find((item) => (item.system_template_id || item.id) === id) : undefined;
}

// The group is an explicit admin choice; changing an activity never guesses it from a title.
export function libraryNewTemplate(kind = "training", groupId = null) {
  const group = TRAINING_GROUPS.find((entry) => entry.id === groupId);
  return libraryDraft("templates", { kind, group_id: kind === "training" && groupId !== UNASSIGNED_TRAINING_GROUP ? groupId : null,
    training_unit_type: kind === "training" ? group?.unitType || "individual_training" : null });
}

export function libraryRunningSelection(draft, choice, items, t) {
  if (!RUNNING_TRAINING_CHOICES.includes(choice)) return {};
  if (runningTrainingChoice(draft) === choice && (draft.training_structure || draft.running_details)) return {};
  const presetFor = (value) => {
    if (!RUNNING_TRAINING_CHOICES.includes(value)) return null;
    const saved = libraryRunningTemplate(items, value);
    if (saved) return saved;
    const factory = STRUCTURED_TRAINING_TEMPLATES.includes(value) ? trainingPreset : runningPreset;
    const de = factory(value, (key) => calendarV2Copy.de[key] || t(key));
    const en = factory(value, (key) => calendarV2Copy.en[key] || t(key));
    return { ...de, title_en: en.title, description_en: en.description,
      icon_id: value === "sprint_acceleration" ? "sprint" : value === "tennis_interval" ? "tennis" : value === "sidestep" ? "coordination" : "running" };
  };
  const record = libraryRunningTemplate(items, choice);
  const preset = presetFor(choice), previous = presetFor(runningTrainingChoice(draft));
  const patch = { training_unit_type: preset.training_unit_type || preset.trainingUnitType,
    running_variant: preset.running_variant || null, running_details: preset.running_details ? cloneLibraryValue(preset.running_details) : null,
    training_template: preset.training_template || null, training_structure: preset.training_structure ? cloneLibraryValue(preset.training_structure) : null,
    recovery_type: null, default_duration_minutes: record ? record.default_duration_minutes : preset.duration,
    planned_exertion: preset.planned_exertion ?? preset.running_details?.planned_exertion ?? null };
  if (patch.running_details) delete patch.running_details.measured_hrmax;
  // Changing a variant must not replace authored bilingual text or its chosen group.
  for (const key of textFields) if ((!draft[key]?.trim() || (previous?.[key] && draft[key] === previous[key])) && preset[key]) patch[key] = preset[key];
  if (!draft.title?.trim() || (previous?.icon_id && draft.icon_id === previous.icon_id)) patch.icon_id = preset.icon_id;
  return patch;
}

export function libraryDraft(type, item) {
  const defaults = { title: "", title_en: "", description: "", description_en: "", status: type === "groups" ? "published" : "draft", icon_id: "training" };
  if (type === "templates") Object.assign(defaults, {
    kind: "training", group_id: null, short_description: "", short_description_en: "", default_duration_minutes: null,
    planned_exertion: null, training_unit_type: "individual_training", training_structure: null, video_id: null, player_may_reschedule: false,
  });
  if (type === "exercises") Object.assign(defaults, { work: null, default_rest_seconds: null, video_id: null });
  return cloneLibraryValue({ ...defaults, ...(type === "templates" && item?.id ? { training_unit_type: item.training_unit_type ?? null } : {}), ...item });
}

export function libraryPayload(type, draft, status = draft.status) {
  const payload = { title: draft.title.trim(), title_en: draft.title_en.trim(), status };
  if (type !== "groups") Object.assign(payload, { description: draft.description.trim(), description_en: draft.description_en.trim(), icon_id: draft.icon_id, video_id: draft.video_id || null });
  if (type === "templates") {
    Object.assign(payload, { kind: draft.kind, group_id: draft.group_id || null, short_description: draft.short_description.trim(), short_description_en: draft.short_description_en.trim(),
      default_duration_minutes: draft.default_duration_minutes === "" || draft.default_duration_minutes == null ? null : Number(draft.default_duration_minutes),
      planned_exertion: draft.planned_exertion || null, player_may_reschedule: draft.player_may_reschedule === true });
    for (const key of activityKeys) payload[key] = draft[key] ?? null;
    if (payload.running_details) { payload.running_details = { ...payload.running_details }; delete payload.running_details.measured_hrmax; }
  }
  if (type === "exercises") Object.assign(payload, { work: draft.work || null, default_rest_seconds: draft.default_rest_seconds === "" ? null : draft.default_rest_seconds });
  if (draft.id) payload.expected_version = draft.version;
  return payload;
}

export function libraryValidation(type, draft) {
  if (!draft.title.trim()) return "nameRequired";
  if (type === "templates") {
    const duration = draft.default_duration_minutes;
    if (duration !== "" && duration != null && (!Number.isFinite(Number(duration)) || Number(duration) <= 0 || Number(duration) > 1440)) return "invalidDuration";
    if (draft.training_structure) {
      const compiled = compileTrainingStructure(draft.training_structure);
      if (!compiled.ok) return "invalidStructure";
      if (duration !== "" && duration != null) {
        const plannedMs = Number(duration) * 60000;
        if (compiled.plan.totalDurationMs === null ? plannedMs <= compiled.plan.knownDurationMs : Math.abs(plannedMs - compiled.plan.totalDurationMs) > 0.06) return "durationMismatch";
      }
    }
    if (draft.running_variant && !draft.training_structure && runningValidation({ ...draft, trainingUnitType: draft.training_unit_type, duration })) return "invalidStructure";
  }
  if (type === "exercises") {
    const work = draft.work;
    const valid = (value, min, max) => typeof value === "number" && Number.isFinite(value) && value >= min && value <= max;
    if (work && !(work.kind === "time" ? valid(work.seconds, 1, 7200) : work.kind === "distance" ? valid(work.distance_m, 1, 1000) : work.kind === "repetitions" && valid(work.count, 1, 1000) && Number.isInteger(work.count))) return "invalidWork";
    if (draft.default_rest_seconds != null && draft.default_rest_seconds !== "" && !valid(draft.default_rest_seconds, 0, 7200)) return "invalidWork";
  }
  if (textFields.some((key) => draft[key] != null && typeof draft[key] !== "string")) return "nameRequired";
  return null;
}

// Keep work empty until the admin provides an actual target.
export function blankLibraryStructure() {
  return { version: 2, warmup_seconds: null, cooldown_seconds: null, runout_seconds: null,
    blocks: [{ id: "block-1", sets: 1, set_rest_seconds: 0, intervals: [{ id: "interval-1", repetitions: 1, work: { kind: "time", seconds: "" }, rest_seconds: 0 }] }] };
}

export function libraryResponse(data, type) {
  return data?.[{ templates: "template", exercises: "exercise", groups: "group" }[type]] || data;
}
